import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData, { formatPrice, siteConfig } from "../Datas/productData";

const categories = {
  "Art Supplies": [
    "Watercolour Paint",
    "Acrylic Paint",
    "Gouache",
    "Pastels",
    "Charcoal",
    "Brushes",
    "Studio",
  ],
  "Hobby & Creativity": [
    "Calligraphy",
    "Organise",
    "Tools & Accessories",
    "Home Office",
  ],
  Pens: [
    "Fine Writing",
    "Gel Pens",
    "Mechanical Pencils",
    "Fineliners",
    "Brush Pens",
    "Illustration Markers",
    "Fountain Pen Ink",
  ],
  "Paper & Pads": ["Sketchbooks", "Watercolour Pads", "Notebooks & Journals"],
  Kids: ["Kids' Pens"],
};

const moreLinks = [
  ["About us", "/about"],
  ["FAQs", "/faqs"],
  ["Delivery & payment", "/shipping"],
  ["Returns", "/returns"],
  ["Privacy policy", "/privacy"],
  ["Cookie policy", "/cookies"],
  ["Terms & conditions", "/terms"],
];

function Header() {
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const {
    cartItems,
    cartCount,
    wishlist,
    isCartOpen,
    closeCart,
    openCart,
    removeFromCart,
    updateQuantity,
  } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [announcement, setAnnouncement] = useState(0);

  const announcements = [
    siteConfig.shipping.message,
    "Secure checkout with supported digital and card payments",
  ];

  useEffect(() => {
    const timer = window.setInterval(
      () => setAnnouncement((current) => (current + 1) % announcements.length),
      4000
    );
    return () => window.clearInterval(timer);
  }, [announcements.length]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
        setMoreOpen(false);
        closeCart();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeCart]);

  const results = search.trim()
    ? productData
        .filter((product) =>
          `${product.brand} ${product.name} ${
            product.category
          } ${product.tags.join(" ")}`
            .toLowerCase()
            .includes(search.trim().toLowerCase())
        )
        .slice(0, 5)
    : [];

  const submitSearch = (event) => {
    event.preventDefault();
    const term = search.trim();
    navigate(term ? `/shop?search=${encodeURIComponent(term)}` : "/shop");
    setSearchOpen(false);
    setMenuOpen(false);
    setMoreOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToCategory = (category, subcategory) => {
    const query = new URLSearchParams();
    if (category === "New arrivals") query.set("new", "true");
    else if (category) query.set("category", category);
    if (subcategory) query.set("subcategory", subcategory);
    setMenuOpen(false);
    setMoreOpen(false);
    navigate(`/shop?${query.toString()}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cartSubtotal = cartItems.reduce((total, item) => {
    const product = productData.find((entry) => entry.id === item.id);
    return product ? total + product.price * item.quantity : total;
  }, 0);

  return (
    <>
      <header className="site-header" ref={headerRef}>
        <div className="site-header__announcement" aria-live="polite">
          {announcements[announcement]}
        </div>

        <div className="site-header__utility">
          <span>Prices are displayed in USD</span>
          <span>
            <Link to="/contact">Customer service</Link>
            <i aria-hidden="true">|</i> {siteConfig.operatingCountry} ({siteConfig.currency})
          </span>
        </div>

        <div className="site-header__main">
          <button
            type="button"
            className="site-header__menu-button"
            aria-label="Open navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <FiMenu />
          </button>

          <Link
            className="site-header__logo"
            to="/"
            aria-label={`${siteConfig.brandName} home`}
          >
            <img
              src="/head/poodliving-logo.svg"
              alt=""
              width="250"
              height="52"
            />
          </Link>

          <nav className="site-header__nav" aria-label="Primary navigation">
            <Link className="site-header__nav-link" to="/shop">
              Shop all
            </Link>
            {siteConfig.nav.map((item) => (
              <div className="site-header__nav-item" key={item.label}>
                <button
                  type="button"
                  onClick={() => goToCategory(item.category)}
                >
                  {item.label}
                </button>
                {categories[item.category] && (
                  <div className="site-header__mega">
                    <div className="site-header__mega-heading">
                      <span>{item.label}</span>
                      <button
                        type="button"
                        onClick={() => goToCategory(item.category)}
                      >
                        Shop all →
                      </button>
                    </div>
                    <div className="site-header__mega-links">
                      {categories[item.category].map((subcategory) => (
                        <button
                          type="button"
                          key={subcategory}
                          onClick={() =>
                            goToCategory(item.category, subcategory)
                          }
                        >
                          {subcategory}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="site-header__nav-item site-header__nav-item--more">
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen((open) => !open)}
              >
                More <FiChevronDown aria-hidden="true" />
              </button>
              <div
                className={`site-header__more-menu ${
                  moreOpen ? "is-open" : ""
                }`}
              >
                {moreLinks.map(([label, to]) => (
                  <Link key={to} to={to} onClick={() => setMoreOpen(false)}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          <div className="site-header__icons">
            <Link
              className="site-header__icon"
              to="/shop?wishlist=true"
              aria-label={`Wishlist with ${wishlist.length} items`}
            >
              <FiHeart />
              {wishlist.length > 0 && <span>{wishlist.length}</span>}
            </Link>
            <button
              type="button"
              className="site-header__icon site-header__cart-button"
              aria-label={`Open cart with ${cartCount} items`}
              onClick={openCart}
            >
              <FiShoppingBag />
              {cartCount > 0 && <span>{cartCount}</span>}
            </button>
          </div>
        </div>

        <form
          className="site-header__search"
          role="search"
          onSubmit={submitSearch}
        >
          <FiSearch aria-hidden="true" />
          <label className="sr-only" htmlFor="global-product-search">
            Product search
          </label>
          <input
            id="global-product-search"
            type="search"
            value={search}
            placeholder="Product Search"
            autoComplete="off"
            onFocus={() => setSearchOpen(true)}
            onChange={(event) => {
              setSearch(event.target.value);
              setSearchOpen(true);
            }}
          />
          <button type="submit">Search</button>
          {searchOpen && search.trim() && (
            <div
              className="site-header__search-results"
              aria-label="Search suggestions"
            >
              {results.length > 0 ? (
                <>
                  {results.map((product) => (
                    <Link
                      key={product.id}
                      to={`/shopDetails/${product.id}`}
                      onClick={() => setSearchOpen(false)}
                    >
                      <img src={product.image} alt="" />
                      <span>
                        <b>{product.brand}</b>
                        {product.name}
                      </span>
                      <strong>{formatPrice(product.price)}</strong>
                    </Link>
                  ))}
                  <button type="submit">
                    View all results for “{search.trim()}” →
                  </button>
                </>
              ) : (
                <p>No products match “{search.trim()}”.</p>
              )}
            </div>
          )}
        </form>
      </header>

      <div
        className={`site-header__mobile-menu ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <div className="site-header__drawer-title">
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation"
          >
            <FiX />
          </button>
          <strong>Menu</strong>
        </div>
        <nav aria-label="Mobile navigation">
          <Link to="/shop" onClick={() => setMenuOpen(false)}>
            Shop all
            <span>→</span>
          </Link>
          {siteConfig.nav.map((item) => (
            <div key={item.label}>
              <button type="button" onClick={() => goToCategory(item.category)}>
                {item.label}
                <span>→</span>
              </button>
              {categories[item.category]?.map((subcategory) => (
                <button
                  className="site-header__mobile-child"
                  type="button"
                  key={subcategory}
                  onClick={() => goToCategory(item.category, subcategory)}
                >
                  {subcategory}
                </button>
              ))}
            </div>
          ))}
          <div className="site-header__mobile-more">
            <strong>More</strong>
            {moreLinks.map(([label, to]) => (
              <Link key={to} to={to} onClick={() => setMenuOpen(false)}>
                {label}
              </Link>
            ))}
          </div>
          <Link to="/contact" onClick={() => setMenuOpen(false)}>
            Customer service
          </Link>
        </nav>
      </div>

      <aside
        className={`site-header__cart-drawer ${isCartOpen ? "is-open" : ""}`}
        aria-hidden={!isCartOpen}
      >
        <div className="site-header__drawer-title">
          <button type="button" onClick={closeCart} aria-label="Close cart">
            <FiX />
          </button>
          <strong>Cart ({cartCount})</strong>
        </div>
        {cartItems.length === 0 ? (
          <div className="site-header__cart-empty">
            <FiShoppingBag />
            <h2>Your cart is empty</h2>
            <Link to="/shop" onClick={closeCart}>
              Start shopping
            </Link>
          </div>
        ) : (
          <>
            <div className="site-header__cart-items">
              {cartItems.map((item) => {
                const product = productData.find(
                  (entry) => entry.id === item.id
                );
                if (!product) return null;
                return (
                  <article key={`${item.id}-${item.size}-${item.color}`}>
                    <Link to={`/shopDetails/${product.id}`} onClick={closeCart}>
                      <img src={product.image} alt={product.name} />
                    </Link>
                    <div>
                      <span>{product.brand}</span>
                      <Link
                        to={`/shopDetails/${product.id}`}
                        onClick={closeCart}
                      >
                        {product.name}
                      </Link>
                      <small>
                        {item.size} · {item.color}
                      </small>
                      <div className="site-header__cart-qty">
                        <button
                          type="button"
                          aria-label={`Decrease ${product.name} quantity`}
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity - 1
                            )
                          }
                        >
                          −
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          aria-label={`Increase ${product.name} quantity`}
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity + 1
                            )
                          }
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            removeFromCart(item.id, item.size, item.color)
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <strong>
                      {formatPrice(product.price * item.quantity)}
                    </strong>
                  </article>
                );
              })}
            </div>
            <div className="site-header__cart-total">
              <div>
                <span>Total</span>
                <strong>{formatPrice(cartSubtotal)}</strong>
              </div>
              <small>Shipping calculated at checkout</small>
              <Link to="/checkout" onClick={closeCart}>
                Checkout
              </Link>
              <Link to="/cartpage" onClick={closeCart}>
                View cart
              </Link>
            </div>
          </>
        )}
      </aside>

      {(menuOpen || isCartOpen) && (
        <button
          type="button"
          className="site-header__scrim"
          aria-label="Close overlay"
          onClick={() => {
            setMenuOpen(false);
            closeCart();
          }}
        />
      )}
    </>
  );
}

export default Header;
